import { useState, useEffect } from 'react';
import { Routes, Route } from 'react-router-dom';
import { SignedIn, SignedOut } from '@clerk/clerk-react';
import SignInPage from './pages/SignInPage';
import SignUpPage from './pages/SignUpPage';
import PinLoginPage from './pages/PinLoginPage';
import AuthenticatedApp from './AuthenticatedApp';
import App from './App';
import { getApiBaseUrl } from './constants';

type AuthState = 'checking' | 'token' | 'none';

function AppRouter() {
  const [authState, setAuthState] = useState<AuthState>('checking');
  const [showPinLogin, setShowPinLogin] = useState(false);

  useEffect(() => {
    checkTokenAuth();
  }, []);

  const checkTokenAuth = async () => {
    const accessToken = localStorage.getItem('hw_access_token');
    if (!accessToken) {
      setAuthState('none');
      return;
    }
    try {
      const res = await fetch(`${getApiBaseUrl()}/me`, {
        headers: { 'Authorization': `Bearer ${accessToken}` },
      });
      if (res.ok) {
        const profile = await res.json();
        if (profile && profile.id) {
          setAuthState('token');
          return;
        }
      }
      // Invalid token, clear it
      localStorage.removeItem('hw_access_token');
    } catch {
      // Token auth failed, fall through to Clerk/PIN
    }
    setAuthState('none');
  };

  // Still checking for token cookie
  if (authState === 'checking') {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', background: '#0a0a0f' }}>
        <div style={{ width: 40, height: 40, border: '3px solid rgba(124,58,237,0.2)', borderTopColor: '#7c3aed', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  // Token-authed (cookie) - render app directly, no Clerk needed
  if (authState === 'token') {
    return <App />;
  }

  // Not token-authed - show Clerk flow or PIN login
  return (
    <Routes>
      <Route path="/sign-in/*" element={<SignInPage />} />
      <Route path="/sign-up/*" element={<SignUpPage />} />
      <Route path="/s/:token" element={null} /> {/* Handled server-side */}
      <Route
        path="/*"
        element={
          <>
            <SignedIn>
              <AuthenticatedApp />
            </SignedIn>
            <SignedOut>
              {showPinLogin ? (
                <PinLoginPage />
              ) : (
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minHeight: '100vh',
                  background: '#0a0a0f',
                  fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
                  gap: '1rem',
                }}>
                  <h1 style={{ color: '#f0f0f5', fontSize: '1.4rem', fontWeight: 600, marginBottom: 0 }}>
                    Homework Tracker
                  </h1>
                  <p style={{ color: '#8b8b9e', fontSize: '0.85rem', margin: 0 }}>
                    Choose how to sign in
                  </p>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', width: 280, marginTop: '0.5rem' }}>
                    <button
                      onClick={() => setShowPinLogin(true)}
                      style={{
                        padding: '14px',
                        background: 'linear-gradient(135deg, #7c3aed 0%, #f59e0b 100%)',
                        color: 'white',
                        border: 'none',
                        borderRadius: 10,
                        fontSize: '1rem',
                        fontWeight: 600,
                        cursor: 'pointer',
                        fontFamily: 'inherit',
                      }}
                    >
                      Sign in with PIN
                    </button>
                    <button
                      onClick={() => window.location.href = '/sign-in'}
                      style={{
                        padding: '14px',
                        background: 'rgba(255,255,255,0.05)',
                        color: '#8b8b9e',
                        border: '1px solid rgba(255,255,255,0.1)',
                        borderRadius: 10,
                        fontSize: '0.9rem',
                        cursor: 'pointer',
                        fontFamily: 'inherit',
                      }}
                    >
                      Parent sign in
                    </button>
                  </div>
                </div>
              )}
            </SignedOut>
          </>
        }
      />
    </Routes>
  );
}

export default AppRouter;
